/**
 * Onboarding Tracker Utility Functions
 *
 * Progressive onboarding system that tracks user completion state
 * through granular steps instead of boolean flags.
 */

import {
  OnboardingStepId,
  OnboardingStepDefinition,
  ONBOARDING_STEPS,
  getStepsForDomain,
  getRequiredStepsForDomain,
  DomainType,
} from '../config/onboarding.config';
import { Step, StepStatus } from '../guards/admin/types';

/**
 * Check if user has completed all required onboarding steps for their domain
 */
export function hasCompletedOnboarding(
  onboardingSteps: Step[] | undefined,
  domain: DomainType
): boolean {
  if (!onboardingSteps || onboardingSteps.length === 0) {
    return false;
  }

  const requiredSteps = getRequiredStepsForDomain(domain);
  const requiredStepIds = new Set(requiredSteps.map((step) => step.id));

  // Check if all required steps are completed
  const completedRequiredSteps = onboardingSteps.filter(
    (step) => requiredStepIds.has(step.stepId) && step.status === 'done'
  );

  return completedRequiredSteps.length === requiredSteps.length;
}

/**
 * Inverse of hasCompletedOnboarding - determines if user needs onboarding
 * This replaces the isNewUser boolean flag
 *
 * BACKWARD COMPATIBILITY: If isNewUser is false, user is considered already onboarded
 * (migrated from old system). This prevents re-onboarding existing users.
 */
export function needsOnboarding(
  onboardingSteps: Step[] | undefined,
  domain: DomainType,
  isNewUser?: boolean
): boolean {
  // Backward compatibility: If isNewUser is explicitly false, user is already onboarded
  // This handles users from the old system who completed onboarding before migration
  // For new users, isNewUser will be undefined and we use progressive onboarding system
  if (isNewUser === false) {
    return false;
  }

  return !hasCompletedOnboarding(onboardingSteps, domain);
}

/**
 * Get the next step the user should complete
 */
export function getNextStep(
  onboardingSteps: Step[] | undefined,
  domain: DomainType
): OnboardingStepDefinition | null {
  const allSteps = getStepsForDomain(domain);
  const stepMap = new Map(onboardingSteps?.map((s) => [s.stepId, s]) || []);

  // Find first incomplete required step
  for (const stepDef of allSteps) {
    if (!stepDef.required) continue;

    const userStep = stepMap.get(stepDef.id);
    const status = userStep?.status || 'pending';

    // Check prerequisites
    const prerequisitesMet = stepDef.prerequisite?.every((prereqId) => {
      const prereqStep = stepMap.get(prereqId);
      return prereqStep?.status === 'done';
    }) ?? true;

    if (status !== 'done' && status !== 'skipped' && prerequisitesMet) {
      return stepDef;
    }
  }

  // All required steps done, find first incomplete optional step
  for (const stepDef of allSteps) {
    if (stepDef.required) continue;

    const userStep = stepMap.get(stepDef.id);
    const status = userStep?.status || 'pending';

    const prerequisitesMet = stepDef.prerequisite?.every((prereqId) => {
      const prereqStep = stepMap.get(prereqId);
      return prereqStep?.status === 'done';
    }) ?? true;

    if (status !== 'done' && status !== 'skipped' && prerequisitesMet) {
      return stepDef;
    }
  }

  return null;
}

/**
 * Calculate onboarding progress percentage
 */
export function getOnboardingProgress(
  onboardingSteps: Step[] | undefined,
  domain: DomainType
): {
  completedRequired: number;
  totalRequired: number;
  completedOptional: number;
  totalOptional: number;
  requiredPercentage: number;
  overallPercentage: number;
} {
  const allSteps = getStepsForDomain(domain);
  const stepMap = new Map(onboardingSteps?.map((s) => [s.stepId, s]) || []);

  const requiredSteps = allSteps.filter((s) => s.required);
  const optionalSteps = allSteps.filter((s) => !s.required);

  const completedRequired = requiredSteps.filter(
    (s) => stepMap.get(s.id)?.status === 'done'
  ).length;

  const completedOptional = optionalSteps.filter(
    (s) => stepMap.get(s.id)?.status === 'done'
  ).length;

  const totalRequired = requiredSteps.length;
  const totalOptional = optionalSteps.length;

  const requiredPercentage = totalRequired > 0 ? (completedRequired / totalRequired) * 100 : 100;

  const totalSteps = totalRequired + totalOptional;
  const completedSteps = completedRequired + completedOptional;
  const overallPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 100;

  return {
    completedRequired,
    totalRequired,
    completedOptional,
    totalOptional,
    requiredPercentage,
    overallPercentage,
  };
}

/**
 * Initialize onboarding steps for a new user
 */
export function initializeOnboardingSteps(domain: DomainType): Step[] {
  const domainSteps = getStepsForDomain(domain);

  return domainSteps.map((stepDef) => ({
    stepId: stepDef.id,
    status: 'pending' as StepStatus,
    required: stepDef.required,
    blocks: stepDef.blocks,
  }));
}

/**
 * Mark a step as complete and update dependent steps
 */
export function completeStep(
  onboardingSteps: Step[],
  stepId: OnboardingStepId
): Step[] {
  const stepDef = ONBOARDING_STEPS[stepId];
  if (!stepDef) return onboardingSteps;

  const stepMap = new Map(onboardingSteps.map((s) => [s.stepId, s]));

  // Mark the step as done
  const targetStep = stepMap.get(stepId);
  if (targetStep) {
    targetStep.status = 'done';
  }

  // Update steps that were blocked by this step
  if (stepDef.blocks) {
    stepDef.blocks.forEach((blockedStepId) => {
      const blockedStep = stepMap.get(blockedStepId);
      if (blockedStep && blockedStep.status === 'blocked') {
        // Check if all prerequisites are met
        const blockedStepDef = ONBOARDING_STEPS[blockedStepId];
        const allPrereqsMet = blockedStepDef.prerequisite?.every((prereqId) => {
          const prereqStep = stepMap.get(prereqId);
          return prereqStep?.status === 'done';
        }) ?? true;

        if (allPrereqsMet) {
          blockedStep.status = 'pending';
        }
      }
    });
  }

  return Array.from(stepMap.values());
}

/**
 * Skip an optional step
 */
export function skipStep(onboardingSteps: Step[], stepId: OnboardingStepId): Step[] {
  const stepDef = ONBOARDING_STEPS[stepId];
  if (!stepDef || stepDef.required) {
    // Cannot skip required steps
    return onboardingSteps;
  }

  const stepMap = new Map(onboardingSteps.map((s) => [s.stepId, s]));
  const targetStep = stepMap.get(stepId);

  if (targetStep) {
    targetStep.status = 'skipped';
  }

  return Array.from(stepMap.values());
}

/**
 * Get steps grouped by category
 */
export function getStepsByCategory(
  onboardingSteps: Step[] | undefined,
  domain: DomainType
): Record<string, { definition: OnboardingStepDefinition; userStep?: Step }[]> {
  const domainSteps = getStepsForDomain(domain);
  const stepMap = new Map(onboardingSteps?.map((s) => [s.stepId, s]) || []);

  const grouped: Record<string, { definition: OnboardingStepDefinition; userStep?: Step }[]> = {};

  domainSteps.forEach((stepDef) => {
    if (!grouped[stepDef.category]) {
      grouped[stepDef.category] = [];
    }

    grouped[stepDef.category].push({
      definition: stepDef,
      userStep: stepMap.get(stepDef.id),
    });
  });

  return grouped;
}

/**
 * Estimate remaining time in minutes
 */
export function estimateTimeRemaining(
  onboardingSteps: Step[] | undefined,
  domain: DomainType
): number {
  const domainSteps = getStepsForDomain(domain);
  const stepMap = new Map(onboardingSteps?.map((s) => [s.stepId, s]) || []);

  let totalMinutes = 0;

  domainSteps.forEach((stepDef) => {
    const userStep = stepMap.get(stepDef.id);
    const status = userStep?.status || 'pending';

    // Only count incomplete required steps and pending optional steps
    if (status !== 'done' && status !== 'skipped') {
      if (stepDef.required || status === 'current') {
        totalMinutes += stepDef.estimatedMinutes;
      }
    }
  });

  return totalMinutes;
}

/**
 * Check if a specific step is available (prerequisites met)
 */
export function isStepAvailable(
  onboardingSteps: Step[] | undefined,
  stepId: OnboardingStepId
): boolean {
  const stepDef = ONBOARDING_STEPS[stepId];
  if (!stepDef) return false;

  if (!stepDef.prerequisite || stepDef.prerequisite.length === 0) {
    return true;
  }

  const stepMap = new Map(onboardingSteps?.map((s) => [s.stepId, s]) || []);

  return stepDef.prerequisite.every((prereqId) => {
    const prereqStep = stepMap.get(prereqId);
    return prereqStep?.status === 'done';
  });
}

/**
 * Get current active step (status: 'current')
 */
export function getCurrentStep(onboardingSteps: Step[] | undefined): Step | null {
  if (!onboardingSteps) return null;

  const currentStep = onboardingSteps.find((step) => step.status === 'current');
  return currentStep || null;
}

/**
 * Set a step as current and unset all others
 */
export function setCurrentStep(onboardingSteps: Step[], stepId: OnboardingStepId): Step[] {
  return onboardingSteps.map((step) => ({
    ...step,
    status: step.stepId === stepId ? 'current' : step.status === 'current' ? 'pending' : step.status,
  }));
}

/**
 * Get human-readable status text
 */
export function getStepStatusText(status: StepStatus): string {
  const statusMap: Record<StepStatus, string> = {
    pending: 'Not Started',
    current: 'In Progress',
    done: 'Completed',
    skipped: 'Skipped',
    blocked: 'Blocked',
  };

  return statusMap[status] || 'Unknown';
}
